import { useQueryClient } from "@tanstack/react-query";
import { memo, ReactNode } from "react";
import { conversationQueryKeys } from "../db/collections";
import { useOnNotification } from "../notice/use-on-notification";
import { useGlobalConversationCollections } from "./global-conversation-collections-context";

/**
 * Syncs real-time message notifications to the local TanStack DB.
 *
 * When a message is created server-side (by another user, AI agent, or inbound event),
 * this provider receives the notification and inserts the message into the local
 * collection, enabling real-time UI updates without polling.
 */
export const MessageDbSyncProvider = memo(function MessageDbSyncProvider({
  children,
}: {
  children: ReactNode;
}) {
  const collections = useGlobalConversationCollections();
  const queryClient = useQueryClient();

  useOnNotification("messageCreated", (event) => {
    try {
      collections.messages.utils.writeInsert({
        ...event.message,
        // SSE sends dates as ISO strings - convert to Date
        sentAt: new Date(event.message.sentAt as unknown as string),
        conversationId: event.conversationId,
      });
      void queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.messages(),
      });
    } catch {
      // Collection sync might not be ready yet - message will be fetched on navigation
    }
  });

  return <>{children}</>;
});
